<template>
    <div class="main-container w-full h-full flex column align-center p-20 border-box ">
        <div v-if="loaded" class="w-full h-full flex column content-item">
            <div class="tree-table-header">Templates</div>
            <div class="tree-table-container">
                <table class="tree-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>File</th>
                            <th>Enabled</th>
                            <th>Created At</th>
                            <th>Order</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="folder in folders" :key="folder.key">
                            <tr class="folder-row" @click="toggleFolder(folder.key)">
                                <td colspan="6">
                                    <div class="name-cell flex align-center gap-10">
                                        <span class="chevron" :class="{ open: isOpen(folder.key) }">&#9656;</span>
                                        <span class="folder-icon" :class="{ open: isOpen(folder.key) }"></span>
                                        <span class="bold">{{ folder.name }}</span>
                                        <span class="count">{{ folder.items.length }} item{{ folder.items.length === 1 ? '' : 's' }}</span>
                                    </div>
                                </td>
                            </tr>
                            <template v-if="isOpen(folder.key)">
                                <tr v-for="(file, i) in folder.items" :key="file.type"
                                    class="file-row" :class="{ missing: !file.filename }"
                                    @click="handleRowClick(file)">
                                    <td>
                                        <div class="name-cell nested flex align-center gap-10">
                                            <span class="tree-branch" :class="{ last: i === folder.items.length - 1 }"></span>
                                            <img src="@/assets/excel-file.svg" class="file-icon">
                                            <div class="flex column align-start">
                                                <span>{{ file.label || file.type }}</span>
                                                <span class="sub">{{ file.filename ? Utils.calculateFileSize(file.filesize) : 'No file uploaded' }}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ file.type }}</td>
                                    <td>{{ file.filename ? 'YES' : 'NO' }}</td>
                                    <td>{{ file.enabled ? 'Yes' : 'No' }}</td>
                                    <td>{{ file.created_at ? Utils.renderTime(file.created_at) : 'N/A' }}</td>
                                    <td>{{ file.order }}</td>
                                </tr>
                            </template>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
        <PopupCover v-if="activeTemplate" @close="closePopup">
            <PopupBody @close="closePopup" style="width: 500px">
                <template #header>
                    Template Details - {{ activeTemplate.type }}
                </template>
                <template #body>
                    <div class="template-popup-body flex column gap-10">
                        <InpComponent
                            label="label"
                            class="bg-white"
                            :value="activeTemplate.label || ''"
                            type="text"
                            @inputChange="activeTemplate.label = $event[1]"
                        />
                        <InpComponent
                            label="order"
                            class="bg-white"
                            :value="activeTemplate.order"
                            type="number"
                            @inputChange="activeTemplate.order = $event[1]"
                        />
                        <Toggle
                            :value="activeTemplate.enabled"
                            :options="[
                                { label: 'Enabled', value: true },
                                { label: 'Disabled', value: false },
                            ]"
                            @change="(val) => activeTemplate.enabled = val"
                            :label="'Status'"
                        />  
                        <Toggle
                            :value="activeTemplate.available_on_signup"
                            :options="[
                                { label: 'Enabled', value: true },
                                { label: 'Disabled', value: false },
                            ]"
                            @change="(val) => activeTemplate.available_on_signup = val"
                            :label="'Available on signup'"
                        />  
                        
                      
                        <div v-if="activeTemplate.filename" class="flex column gap-5px align-start">
                            <div class="bold">Current Template:</div>
                            <div class='attachment w-full border-box' 
                                >
                                <img src="@/assets/excel-file.svg" class='fileicon'>
                                <div class="filename">
                                    <div class="name-of-the-file">{{ activeTemplate.filename }}</div>
                                    <div class="size-of-the-file">{{ Utils.calculateFileSize(activeTemplate.filesize) }}</div>
                                </div>
                                <div class="flex-1 flex justify-end gap-10 p-10 pointer">
                                    <img class='download' title="View"
                                        @click="previewOpen = true" src="@/assets/preview.svg">
                                    <img class='download' title="Download"
                                        @click="downloadTemplate()" src="@/assets/download.svg">
                                </div>
                            </div>
                        </div>
                        <div v-else class="w-full text-left">
                            No template file uploaded yet
                        </div>
                        <div v-if="activeTemplate.filename && !showReplace" class="button-section flex gap-10">
                            <button class="button" @click="showReplace = true">Replace Template</button>
                            <button class="button transparent" @click="editorOpen = true">Edit content</button>
                            <button class="button transparent" @click="checkOpen = true">Check template</button>
                        </div>

                        <MultiFileUploader v-if="showReplace || !activeTemplate.filename" type=".xlsx" :single=true @sendFileInfo="getFiles" class="uploadfile" />
                        <div class="text-left bold">Description</div>
                        <CommentSection
                            class="comment-section"
                            :commentSection="activeTemplate.structure"
                            @changeinput="quillContent" 
                        />
                        <div class="button-section flex gap-10 mt-40">
                            <button class="button transparent" @click="closePopup">Cancel</button>
                            <button class="button" :disabled="!saveAvailable" @click="saveChanges()">Save</button>
                        </div>
                    </div>
                </template>
            </PopupBody>
        </PopupCover>
        <TemplatePreview v-if="previewOpen && activeTemplate" :type="activeTemplate.type" :title="activeTemplate.label" @close="previewOpen = false" />
        <TemplateContentEditor v-if="editorOpen && activeTemplate" :type="activeTemplate.type" :title="activeTemplate.label"
            @saved="contentSaved" @close="editorOpen = false" />
        <TemplateCheck v-if="checkOpen && activeTemplate" :type="activeTemplate.type" :title="activeTemplate.label" @close="checkOpen = false" />
    </div>
</template>

<script setup>
    import BO_API from '../api/bo_api.js'
    import API from '@/api/index.js';
    import { ref, onMounted, computed } from 'vue';
    import { FIUTemplatesTypes, BANK_TEMPLATES, PLAYER_TEMPLATES } from '../../../shared/index';
    import PopupBody from '@/components/PopupBody.vue';
    import InpComponent from '@/components/InpComponent.vue';
    import CommentSection from '@/views/CommentSection.vue';
    import PopupCover from '@/components/PopupCover.vue';
    import MultiFileUploader from '@/components/MultifileUploader.vue';
    import Toggle from '@/elements/Toggle.vue';
    import TemplatePreview from '@/components/TemplatePreview.vue';
    import TemplateContentEditor from '@/components/TemplateContentEditor.vue';
    import TemplateCheck from '@/components/TemplateCheck.vue';
    import store from '../store/index.js';
    import Utils from '@/utils/index.js';



    const templates = ref([]);
    const mergedData = ref([]);
    const loaded = ref(false);
    const activeTemplate = ref(null);
    const selectedFile = ref(null);
    const initialState = ref(null);
    const showReplace = ref(false);
    const previewOpen = ref(false); // read-only viewer of the current template file
    const editorOpen = ref(false); // grid editor for the template's example rows
    const checkOpen = ref(false); // columns vs validator rules / XML builder

    // folder (tree) state — all folders expanded by default
    const collapsed = ref(new Set());
    function isOpen(key) { return !collapsed.value.has(key); }
    function toggleFolder(key) {
        const next = new Set(collapsed.value);
        next.has(key) ? next.delete(key) : next.add(key);
        collapsed.value = next;
    }

    const FOLDER_DEFS = [
        { key: 'bank', name: 'Bank Templates', match: (t) => BANK_TEMPLATES.includes(t) },
        { key: 'merchant', name: 'Merchant / PSP', match: (t) => t === FIUTemplatesTypes.BANK_MERCHANT_TRANSFER },
        { key: 'player', name: 'Player Templates', match: (t) => PLAYER_TEMPLATES.includes(t) },
    ];

    const folders = computed(() => {
        const result = FOLDER_DEFS.map(f => ({ ...f, items: mergedData.value.filter(item => f.match(item.type)) }));
        const other = mergedData.value.filter(item => !FOLDER_DEFS.some(f => f.match(item.type)));
        if (other.length) result.push({ key: 'other', name: 'Other', items: other });
        return result;
    });

    const saveAvailable = computed(() => {
        if (!activeTemplate.value) return false;
        const currentState = JSON.stringify(activeTemplate.value);
        const initial = JSON.stringify(initialState.value.find(item => item.type === activeTemplate.value.type));
        if (selectedFile.value) {
            return true;
        }
        return currentState !== initial;
    });

    onMounted(async () => {
        try {
            const response = await BO_API.loadTemplates();
            templates.value = response.templates;
            formatTemplateData();
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    });
    function getFiles(files) {
        selectedFile.value = files[0];
    }

    async function downloadTemplate(){
        await API.downloadTemplate(activeTemplate.value.type, activeTemplate.value.filename);
    }
    function quillContent(event) {
        activeTemplate.value.structure = event.structure;
        activeTemplate.value.content = event.text;
    }
    function formatTemplateData() {
        Object.keys(FIUTemplatesTypes).forEach(type => {
            const template = templates.value.find(t => t.type === type);
            if( template ){
                console.log('Found template for type:', template.order, template.type);
                mergedData.value.push({
                    filename: template.filename,
                    enabled: template.enabled,
                    type: type,
                    filesize: template.filesize,
                    created_at: template.created_at,
                    available_on_signup: template.available_on_signup,
                    order: template.order || 100,
                    label: template.label || null,
                    structure: template.structure || null,
                });
                if (template.structure) {
                    mergedData.value[mergedData.value.length - 1].structure = JSON.parse(template.structure);
                }
            } else {
                mergedData.value.push({
                    filename: null,
                    enabled: false,
                    type: type,
                    order: 100,
                    created_at: null,
                    available_on_signup: false,
                    label: null,
                    structure: null
                });
            }
        });
        mergedData.value.sort((a, b) => a.order - b.order);
        initialState.value = JSON.parse(JSON.stringify(mergedData.value));
        loaded.value = true
    }
    function handleRowClick(item) {
        activeTemplate.value = JSON.parse(JSON.stringify(item));
    }
    function closePopup() {
        activeTemplate.value = null;
        selectedFile.value = null;
        previewOpen.value = false;
        editorOpen.value = false;
        checkOpen.value = false;
    }
    // The editor saved a new file for this template: show its size without marking the popup as changed
    function contentSaved(saved) {
        const type = activeTemplate.value?.type;
        const update = item => { if (item.type === type) item.filesize = saved.filesize; };
        if (activeTemplate.value) update(activeTemplate.value);
        mergedData.value.forEach(update);
        initialState.value.forEach(update);
    }
    async function saveChanges(){
        const templateData = {
            label: activeTemplate.value.label,
            type: activeTemplate.value.type,
            enabled: activeTemplate.value.enabled,
            available_on_signup: activeTemplate.value.available_on_signup,
            order: activeTemplate.value.order,
            structure: activeTemplate.value.structure ? JSON.stringify(activeTemplate.value.structure) : null,
            content: activeTemplate.value.content || null,
        }

        let templateResponse = await BO_API.uploadTemplateFile(selectedFile.value, templateData);
        const templateResp = templateResponse.template;
        activeTemplate.value = {
            id: templateResp._id,
            filename: templateResp.filename,
            enabled: templateResp.enabled,
            label: templateResp.label,
            filesize: templateResp.filesize,
            type: activeTemplate.value.type,
            created_at: templateResp.created_at,
            available_on_signup: templateResp.available_on_signup,
            order: templateResp.order,
        }
        mergedData.value = mergedData.value.map(item => {
            if (item.type === activeTemplate.value.type) {
                return {
                    ...item,
                    order: templateResp.order ?? item.order,
                    structure: templateData.structure ? JSON.parse(templateData.structure) : null,
                    filename: templateResp.filename,
                    enabled: templateResp.enabled,
                    filesize: templateResp.filesize,
                    type: templateResp.type,
                    label: templateResp.label,
                    created_at: templateResp.created_at,
                    available_on_signup: templateResp.available_on_signup
                };
            }
            return item;
        });
        mergedData.value.sort((a, b) => a.order - b.order);
        initialState.value = JSON.parse(JSON.stringify(mergedData.value));
        showReplace.value = false;
        selectedFile.value = null;
        activeTemplate.value = null;

        if (templateResponse.status === 'success') {
            store.dispatch('addNotification', { type: 'success', text: 'Template updated successfully' });
        } else {
            store.dispatch('addNotification', { type: 'error', text: 'Failed to update template' });
        }
       
    }
    


</script>


<style scoped lang="scss">
    // Same centred 1350px column as the AdminTable pages
    .content-item {
        max-width: 1350px;
        padding: 20px 0;
    }
    .table-header {
        font-size: 24px;
        font-weight: bold;
        margin-bottom: 20px;
        text-align: left;
    }
    .filename {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.message-attachments {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.size-of-the-file {
    font-size: 0.8rem;
    color: rgb(116 119 127);
}
.name-of-the-file {
    display: flex;
    align-items: center;
    color: black;
    font-size: 0.9rem;

    text-align: left;
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
}
.template-popup-body {
    max-height: 80vh;
    overflow-y: auto;
}
.fileicon {
    width: 40px;
    height: 40px;
}

.attachment {
    border-radius: .5rem;
    border-width: 1px;
    -moz-column-gap: .5rem;
    column-gap: .5rem;
    grid-template-columns: fit-content(100%) 1fr;
    border-color: rgb(224 226 236);
    background-color: rgb(255 255 255);
    padding: .8rem;


    display: flex;
    align-items: center;
}
.comment-section {
    height: 200px;
    &:deep(.editor-section) {
        height: 70vh;
    }
}

/* ---------- tree table ---------- */
.tree-table-header {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 20px;
    text-align: left;
    color: black;
}
.tree-table-container {
    max-width: 1350px;
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 10px;
    overflow: hidden;
    background: white;
}
.tree-table {
    width: 100%;
    border-collapse: collapse;
    th, td {
        padding: 14px 15px;
        text-align: left;
        color: black;
    }
    tr { border-top: 1px solid #ccc; }
    thead tr { border-top: none; }
    tbody tr { cursor: pointer; }
    .folder-row {
        background: rgb(247 248 251);
        &:hover { background: rgb(238 241 248); }
        .count { font-size: 0.8rem; color: rgb(116 119 127); }
    }
    .file-row {
        border-top-color: rgb(236 237 242);
        td { padding-top: 10px; padding-bottom: 10px; }
        &:hover { background: rgb(247 248 251); }
        &.missing .file-icon { filter: grayscale(1); opacity: 0.35; }
        .sub { font-size: 0.75rem; color: rgb(116 119 127); }
    }
}
.name-cell.nested { padding-left: 14px; }
.chevron {
    display: inline-block;
    width: 12px;
    color: rgb(116 119 127);
    transition: transform 0.15s;
    &.open { transform: rotate(90deg); }
}
/* └ / ├ connector lines for the tree */
.tree-branch {
    position: relative;
    width: 14px;
    align-self: stretch;
    flex-shrink: 0;
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: -10px;
        bottom: -10px;
        border-left: 1px solid rgb(200 203 212);
    }
    &.last::before { bottom: 50%; }
    &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        width: 12px;
        border-top: 1px solid rgb(200 203 212);
    }
}
.file-icon { width: 28px; height: 28px; }
.folder-icon {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
    width: 20px;
    height: 14px;
    margin-top: 3px;
    background: #f2c14e;
    border-radius: 3px;
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: -3px;
        width: 9px;
        height: 4px;
        background: #e0a92e;
        border-radius: 3px 3px 0 0;
    }
    &.open { background: #f7d27a; }
}

</style>